import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { RefreshCw, ShieldX } from 'lucide-react';
import axios from 'axios';
import { cachedGet } from '../utils/cachedGet';
import RegistrationForm from './RegistrationForm';
import WelcomeGate from './WelcomeGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const KEY_INTENT = 'bebida-bacana-login-intent'; // 'consumidor_final' | 'comercio'

/**
 * AppGate — Google-only, sin guest mode.
 *
 *   Sin user            → WelcomeGate (elige Particular o Comercio → Google OAuth)
 *   User + intent='consumidor_final' + sin customer_type
 *                       → auto-set customer_type='consumidor_final' (sin formulario)
 *   User + intent='comercio' + sin customer_type
 *                       → RegistrationForm (formulario comercial completo)
 *   User + customer_type ya seteado
 *                       → catálogo (children)
 *
 * Los datos de envío (dirección, teléfono, número) se piden en el
 * checkout del primer pedido, no acá.
 */
const AppGate = ({ children }) => {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  useEffect(() => { fetchStoreSettings(); }, []);

  // Cuando el usuario vuelve del OAuth y todavía no tiene customer_type,
  // decidimos qué hacer según el "intent" que guardamos en localStorage
  // ANTES de arrancar el OAuth.
  useEffect(() => {
    if (authLoading || !user) return;

    if (user.customer_type) {
      // Ya está clasificado → mantener perfil local sincronizado
      setShowRegistration(false);
      const profileData = {
        firstName: user.firstName || user.name?.split(' ')[0] || '',
        lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        customerType: user.customer_type,
      };
      try {
        localStorage.setItem('bebida-bacana-profile', JSON.stringify(profileData));
        if (user.phone || user.address || user.city) {
          // 🔒 Preservar campos existentes (paymentMethod, shippingMethod,
          // notes, cuit, requiresInvoice, etc.). Antes se sobreescribía el
          // objeto completo con sólo 4 keys, destruyendo la forma de pago
          // elegida y provocando que los descuentos "parpadeen" entre
          // recargas (bug reportado por el usuario: "aparece/desaparece").
          let prevShipping = {};
          try {
            const raw = localStorage.getItem('bebida-bacana-shipping');
            if (raw) prevShipping = JSON.parse(raw) || {};
          } catch { /* ignore */ }
          localStorage.setItem('bebida-bacana-shipping', JSON.stringify({
            ...prevShipping,
            customerName: `${profileData.firstName} ${profileData.lastName}`.trim(),
            phone: user.phone || prevShipping.phone || '',
            address: user.address || prevShipping.address || '',
            city: user.city || prevShipping.city || '',
          }));
        }
      } catch { /* storage lleno */ }
      return;
    }

    // Sin customer_type: decidir por intent
    let intent = null;
    try { intent = localStorage.getItem(KEY_INTENT); } catch { /* storage bloqueado */ }

    if (intent === 'consumidor_final') {
      // Consumidor final: auto-clasificar sin pedir formulario.
      // Los datos de envío se piden recién en checkout.
      if (autoAssigning) return;
      setAutoAssigning(true);
      axios.put(`${API}/auth/customer-type`, { customer_type: 'consumidor_final' }, { withCredentials: true })
        .then(() => {
          // Sólo limpiamos el intent si el PUT tuvo éxito. Si falla
          // (red / 5xx / 401 por sesión expirada), preservamos el
          // intent para que el próximo intento vuelva a intentar la
          // auto-clasificación en vez de mostrar el formulario comercial.
          try { localStorage.removeItem(KEY_INTENT); } catch { /* storage */ }
          return refreshUser();
        })
        .catch((err) => {
          console.warn('[AppGate] auto-classify consumidor_final falló, reintentaremos:', err?.message);
        })
        .finally(() => setAutoAssigning(false));
      return;
    }

    // Todos los demás casos (intent='comercio' o sin intent):
    // mostrar el formulario comercial completo.
    setShowRegistration(true);
  }, [user, authLoading, autoAssigning, refreshUser]);

  const fetchStoreSettings = async () => {
    try {
      const data = await cachedGet(`${API}/store-settings`, { ttl: 60_000 });
      setStoreSettings(data);
    } catch (error) {
      setStoreSettings({ appEnabled: true, loginRequired: true });
    } finally {
      setLoading(false);
    }
  };

  // Auto-elevación de admin: si el user viene con role='admin' desde el
  // backend (por SUPER_ADMIN_EMAILS o promoción manual), le seteamos el
  // flag localStorage y lo redirigimos directo al panel. No hace falta
  // que pase por el TOTP tradicional.
  useEffect(() => {
    if (user?.role === 'admin' && typeof window !== 'undefined'
        && localStorage.getItem('bebida-bacana-admin') !== 'true') {
      localStorage.setItem('bebida-bacana-admin', 'true');
      const path = window.location.pathname;
      // Solo redirigir automáticamente si el admin cayó en la landing
      // o /account. Si ya estaba navegando otra ruta, respetamos.
      if (path === '/' || path === '' || path === '/account' || path === '/products') {
        window.location.replace('/admin');
      }
    }
  }, [user]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: theme.primary }}></div>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || localStorage.getItem('bebida-bacana-admin') === 'true';

  // App disabled — allow admin access
  if (storeSettings && !storeSettings.appEnabled) {
    if (!isAdmin) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.background }}>
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <RefreshCw size={40} className="mx-auto mb-4 text-slate-400" />
            <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>
              Estamos Actualizando
            </h1>
            <p className="mb-4" style={{ color: theme.textSecondary }}>
              {storeSettings.appDisabledMessage || 'Volvemos pronto.'}
            </p>
            <button onClick={() => window.location.reload()} className="px-6 py-3 rounded-lg text-white font-medium" style={{ backgroundColor: theme.primary }}>
              Reintentar
            </button>
          </div>
        </div>
      );
    }
  }

  // Restricted access → sólo comercios
  if (storeSettings?.restrictedAccess && user?.customer_type === 'consumidor_final') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.background }}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <ShieldX size={40} className="mx-auto mb-4 text-orange-500" />
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>
            Acceso Exclusivo Comercios
          </h1>
          <p className="mb-4" style={{ color: theme.textSecondary }}>
            Plataforma disponible solo para comercios registrados.
          </p>
          <a href={`https://wa.me/${storeSettings?.storePhone || '543571522400'}?text=Hola! Tengo un comercio y quisiera acceder a Bebida Bacana`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium" style={{ backgroundColor: '#25D366' }}>
            Contactar por WhatsApp
          </a>
        </div>
      </div>
    );
  }

  // Sin sesión Google → WelcomeGate (elige Particular / Comercio → Google OAuth)
  if (!user && !isAdmin) {
    return <WelcomeGate storeSettings={storeSettings} theme={theme} />;
  }

  // Sesión Google recién obtenida pero todavía sin customer_type y con
  // intent 'comercio' (o sin intent) → formulario comercial.
  if (showRegistration && user) {
    return (
      <>
        {children}
        <RegistrationForm onComplete={() => setShowRegistration(false)} />
      </>
    );
  }

  return children;
};

export default AppGate;
